// The audio block's player.
//
// Two shapes, chosen per block (`playerStyle`):
//
//   'button'   a single small "Play audio" pill. For a clip that punctuates a
//              slide — a pronunciation, a sting, a territorial acknowledgement
//              read aloud — where a timeline would be furniture.
//   'waveform' a full-width scrubbable timeline drawn from the clip's own
//              samples, for a clip the class works *through* and the instructor
//              needs to move around inside.
//
// Both drive one hidden <audio> element. The native control set is not used
// because its size, colours and typography are the browser's rather than the
// deck's, and it looks like a foreign object dropped on a slide.
//
// The waveform is real (see audioWaveform.js) or it is absent: a host that will
// not let the app read the bytes gets a plain timeline that scrubs identically,
// not a decorative row of bars that has nothing to do with the audio.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Volume2, AlertCircle } from 'lucide-react';
import { formatTime } from '../utils/contentUtils.js';
import { loadWaveformPeaks, DEFAULT_BUCKETS } from '../utils/audioWaveform.js';
import SafeHtml from '../components/SafeHtml';

export const AUDIO_PLAYER_STYLES = ['button', 'waveform'];

/** Blocks saved before the two styles existed showed a timeline, so that is
 *  what they keep showing. */
export const DEFAULT_AUDIO_PLAYER_STYLE = 'waveform';

export const normalizeAudioPlayerStyle = (value) =>
  AUDIO_PLAYER_STYLES.includes(value) ? value : DEFAULT_AUDIO_PLAYER_STYLE;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const SEEK_STEP_SECONDS = 5;

/**
 * The bars, drawn from the clip's own peaks.
 *
 * SVG rather than canvas: it scales with the slide at any zoom, needs no
 * device-pixel-ratio bookkeeping, and re-colours as playback advances by
 * changing fills rather than repainting a bitmap.
 */
const Waveform = ({ peaks, progress, height }) => {
  const count = peaks.length;
  const played = progress * count;

  return (
    <svg
      className="block w-full"
      style={{ height }}
      viewBox={`0 0 ${count} 100`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {peaks.map((peak, index) => {
        // A floor of 4 keeps silence visible as a hairline rather than a gap,
        // so the track still reads as a track through a pause.
        const barHeight = Math.max(4, peak * 96);
        return (
          <rect
            key={index}
            x={index + 0.15}
            y={(100 - barHeight) / 2}
            width={0.7}
            height={barHeight}
            fill={index < played ? '#2563eb' : '#cbd5e1'}
          />
        );
      })}
    </svg>
  );
};

const FlatTrack = ({ progress, height }) => (
  <div className="flex w-full items-center" style={{ height }} aria-hidden="true">
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-300">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-blue-600"
        style={{ width: `${progress * 100}%` }}
      />
    </div>
  </div>
);

const AudioPlayer = ({
  src,
  description,
  citation,
  playerStyle,
  title
}) => {
  const style = normalizeAudioPlayerStyle(playerStyle);
  const audioRef = useRef(null);
  const trackRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [peaks, setPeaks] = useState(null);

  const label = (title || '').trim();

  // Peaks are only worth fetching for the style that draws them, and only once
  // per clip — loadWaveformPeaks caches, so a deck that uses the same clip on
  // three slides decodes it once.
  useEffect(() => {
    if (style !== 'waveform' || !src) {
      setPeaks(null);
      return undefined;
    }

    let cancelled = false;
    setPeaks(null);
    loadWaveformPeaks(src, { buckets: DEFAULT_BUCKETS })
      .then((measured) => { if (!cancelled) setPeaks(measured); })
      // Expected, not exceptional: a host that serves audio but publishes no
      // CORS headers lands here, and the flat track takes over.
      .catch(() => { if (!cancelled) setPeaks([]); });

    return () => { cancelled = true; };
  }, [src, style]);

  // A new clip is a new player: whatever was playing is not this.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoadError('');
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      const started = audio.play();
      if (started && typeof started.catch === 'function') {
        started.catch(() => setIsPlaying(false));
      }
    } else {
      audio.pause();
    }
  }, []);

  const seekToRatio = useCallback((ratio) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const next = clamp01(ratio) * audio.duration;
    audio.currentTime = next;
    setCurrentTime(next);
  }, []);

  const ratioFromEvent = useCallback((event) => {
    const track = trackRef.current;
    if (!track) return 0;
    const box = track.getBoundingClientRect();
    if (!box.width) return 0;
    return clamp01((event.clientX - box.left) / box.width);
  }, []);

  const handlePointerDown = useCallback((event) => {
    // The block around this one is draggable for reordering. Without this a
    // scrub gesture starts a drag of the whole block instead.
    event.stopPropagation();
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    seekToRatio(ratioFromEvent(event));
  }, [ratioFromEvent, seekToRatio]);

  const handlePointerMove = useCallback((event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    event.stopPropagation();
    seekToRatio(ratioFromEvent(event));
  }, [ratioFromEvent, seekToRatio]);

  const handlePointerUp = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const handleTrackKeyDown = useCallback((event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const total = Number(audio.duration);
    const step = (seconds) => {
      event.preventDefault();
      if (!Number.isFinite(total) || total <= 0) return;
      seekToRatio((audio.currentTime + seconds) / total);
    };

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        step(SEEK_STEP_SECONDS);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        step(-SEEK_STEP_SECONDS);
        break;
      case 'Home':
        event.preventDefault();
        seekToRatio(0);
        break;
      case 'End':
        event.preventDefault();
        seekToRatio(1);
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        togglePlay();
        break;
      default:
        break;
    }
  }, [seekToRatio, togglePlay]);

  const progress = duration > 0 ? clamp01(currentTime / duration) : 0;

  // null means the peaks are still being read; [] means they could not be.
  // The two draw the same flat track, so the difference is announced rather
  // than shown: the control works throughout, its picture is still arriving.
  const isMeasuringWaveform = style === 'waveform' && Boolean(src) && peaks === null;

  const mediaElement = (
    <audio
      ref={audioRef}
      src={src || undefined}
      preload="metadata"
      className="hidden"
      onPlay={() => setIsPlaying(true)}
      onPause={() => setIsPlaying(false)}
      onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      onLoadedMetadata={(event) => {
        const value = Number(event.currentTarget.duration);
        setDuration(Number.isFinite(value) ? value : 0);
        setLoadError('');
      }}
      onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
      onError={() => setLoadError('This clip could not be loaded. Check the link, or re-upload the file.')}
    />
  );

  const playButton = (compact) => (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); togglePlay(); }}
      disabled={!src || Boolean(loadError)}
      aria-label={isPlaying
        ? `Pause${label ? ` ${label}` : ' audio'}`
        : `Play${label ? ` ${label}` : ' audio'}`}
      className={compact
        ? 'inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300'
        : 'flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300'}
    >
      {isPlaying ? <Pause size={compact ? 16 : 20} /> : <Play size={compact ? 16 : 20} />}
      {compact && (isPlaying ? 'Pause audio' : 'Play audio')}
    </button>
  );

  const timeReadout = useMemo(() => (
    <span className="shrink-0 font-mono text-xs tabular-nums text-slate-600">
      {formatTime(currentTime)} / {duration ? formatTime(duration) : '--:--'}
    </span>
  ), [currentTime, duration]);

  const errorNote = loadError ? (
    <p className="mt-2 flex items-start gap-2 text-xs font-semibold text-red-600" role="alert">
      <AlertCircle size={14} className="mt-0.5 shrink-0" />
      {loadError}
    </p>
  ) : null;

  const emptyNote = !src ? (
    <p className="flex items-center gap-2 text-sm text-slate-500">
      <Volume2 size={16} />
      No audio clip yet — add a file or paste a link.
    </p>
  ) : null;

  return (
    <div className="my-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
      {description && (
        <SafeHtml className="mb-3 text-sm italic text-gray-600" html={description} />
      )}

      {mediaElement}

      {emptyNote || (style === 'button' ? (
        <div className="flex flex-wrap items-center gap-3">
          {playButton(true)}
          {label && <span className="min-w-0 truncate text-sm font-medium text-slate-700">{label}</span>}
          {timeReadout}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-4">
            {playButton(false)}
            <div className="min-w-0 flex-1">
              <div
                ref={trackRef}
                role="slider"
                tabIndex={0}
                aria-label={label ? `Scrub ${label}` : 'Scrub audio'}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration) || 0}
                aria-valuenow={Math.round(currentTime)}
                aria-valuetext={`${formatTime(currentTime)} of ${duration ? formatTime(duration) : 'unknown length'}`}
                aria-busy={isMeasuringWaveform}
                draggable={false}
                onDragStart={(event) => event.preventDefault()}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onKeyDown={handleTrackKeyDown}
                className="cursor-pointer touch-none select-none rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {peaks && peaks.length > 0
                  ? <Waveform peaks={peaks} progress={progress} height={56} />
                  : <FlatTrack progress={progress} height={56} />}
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="font-mono text-xs tabular-nums text-slate-600">{formatTime(currentTime)}</span>
                <span className="font-mono text-xs tabular-nums text-slate-600">
                  {duration ? formatTime(duration) : '--:--'}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {errorNote}

      {citation && (
        <SafeHtml
          className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600"
          html={citation}
        />
      )}
    </div>
  );
};

export default AudioPlayer;
